import type { OrgCustomColors } from '@cognitoforms/types/server-types/model/org-custom-colors';
import iro from '@jaames/iro';
import ColorContrastChecker from 'color-contrast-checker';
import { rgbToHex, rgbStringToObject } from '@cognitoforms/utils/color-utils';
import type { OrgAppearanceSettingsMeta } from '@cognitoforms/types/server-types/model/org-appearance-settings-meta';

// Default helpers functions
export function getDefaults() {
	return {
		Text: 'rgb(35,70,82)',
		Background: 'rgb(255,255,255)',
		Primary: 'rgb(9,160,152)',
		Secondary: 'rgb(148,204,63)',
		Important: 'rgb(216,84,39)'
	};
}

export function isDefaultColors(colors: OrgCustomColors) {
	if (!colors)
		return true;
	const defaultColors = getDefaults();
	return colors.Text.replace(/\s+/g, '') === defaultColors.Text
		&& colors.Background.replace(/\s+/g, '') === defaultColors.Background
		&& colors.Primary.replace(/\s+/g, '') === defaultColors.Primary
		&& colors.Secondary.replace(/\s+/g, '') === defaultColors.Secondary
		&& colors.Important.replace(/\s+/g, '') === defaultColors.Important;
}

// Contrast compatibility helper functions
export function isAACompliant(color1: string, color2: string) {
	const colorChecker = new ColorContrastChecker();
	return colorChecker.isLevelAA(rgbToHex(color1), rgbToHex(color2));
}

export function isCustomCompliant(color1: string, color2: string, ratio: number) {
	const colorChecker = new ColorContrastChecker();
	return colorChecker.isLevelCustom(rgbToHex(color1), rgbToHex(color2), ratio);
}

// Updating Custom Theme functions
/**
 * Compare the relative darkness of two colors
 * @param color1 string in the form rgb(r,g,b)
 * @param color2 string in the form rgb(r,g,b)
 * @returns true if color1 is darker than color2, false otherwise
 */
function calculateRelativeDarkness(color1: string, color2: string) {
	const color1Obj = rgbStringToObject(color1);
	const color2Obj = rgbStringToObject(color2);

	const hsv1 = iro.Color.rgbToHsv(color1Obj);
	const hsv2 = iro.Color.rgbToHsv(color2Obj);
	return hsv1.v > hsv2.v;
}

export function calculateIsDark (hasCustomTheme: boolean, useSystemColors: boolean, colors: OrgCustomColors) {
	// If the org has color customization, rely on that first
	if (hasCustomTheme && colors)
		return calculateRelativeDarkness(colors.Text, colors.Background);
	else if (useSystemColors)
		return window.matchMedia('(prefers-color-scheme: dark)').matches;

	return false;
}

export function updateCustomTheme(useSystemColors: boolean, colors: OrgCustomColors) {
	if (!colors)
		return;
	const hasCustomTheme = !isDefaultColors(colors);
	if (hasCustomTheme) {
		document.documentElement.style.setProperty('--text', colors.Text);
		document.documentElement.style.setProperty('--background', colors.Background);
		document.documentElement.style.setProperty('--primary', colors.Primary);
		document.documentElement.style.setProperty('--secondary', colors.Secondary);
		document.documentElement.style.setProperty('--important', colors.Important);

		document.documentElement.setAttribute('data-has-custom-theme', 'true');
	}
	else {
		document.documentElement.style.removeProperty('--text');
		document.documentElement.style.removeProperty('--background');
		document.documentElement.style.removeProperty('--primary');
		document.documentElement.style.removeProperty('--secondary');
		document.documentElement.style.removeProperty('--important');

		document.documentElement.setAttribute('data-has-custom-theme', 'false');
	}

	document.documentElement.setAttribute('data-color-scheme', calculateIsDark(hasCustomTheme, useSystemColors, colors) ? 'dark' : 'light');
}

// Update Default Typeface
export function updateTypeface(useDefaultTypeface: boolean) {
	if (useDefaultTypeface)
		document.documentElement.style.setProperty('--font-display', 'system-ui');
	else
		document.documentElement.style.removeProperty('--font-display');
}

// JTODO update logo

// Update Org Appearance
export function updateOrgAppearance(useSystemColors: boolean, orgAppearanceSettings: OrgAppearanceSettingsMeta) {
	updateCustomTheme(useSystemColors, orgAppearanceSettings?.Colors);
	updateTypeface(orgAppearanceSettings?.UseDefaultTypeface);
}